/**
 * 한 TOC 의 비교 결과 섹션. table pair 는 src | trg 2칼럼으로 나란히,
 * 추가 테이블은 trg 쪽만, 삭제 테이블은 src 쪽만 렌더한다.
 *
 * 레이아웃/컨테이너 디자인은 DSD-DSD 대사와 통일한다(흰 카드 + flex gap-6).
 *
 * 필터(전체/변경 사항)는 항목을 unmount 하지 않고 hidden 클래스로 숨긴다 — 다운로드
 * HTML 의 inline JS 가 같은 DOM 을 toggle 할 수 있도록(data-dl-item / data-dl-has-changes
 * / data-dl-section-group). 섹션/카드 래퍼에 data-section-id 등을 부여한다.
 */

import { memo, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import type {
  PreviewDiffTable as PreviewDiffTableModel,
  PreviewDiffTocChunk,
} from "@/apis/compare/route";
import { PreviewDiffTable } from "./preview-diff-table";
import { pairHasChanges, tocHasChanges } from "./preview-diff-changes";

export type PreviewDiffViewMode = "all" | "changes";

// DSD-DSD 대사의 카드와 동일 (bg-white + border + rounded-2xl + shadow).
const CARD_CLASS = cn(
  "bg-white border border-line-normal rounded-2xl overflow-clip px-3 py-5",
  "shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1),0px_4px_24px_4px_rgba(0,0,0,0.08)]",
  "[content-visibility:auto] [contain-intrinsic-size:auto_500px]",
);

const Panel = ({
  label,
  table,
  pairTableId,
  side,
}: {
  label: string;
  table: PreviewDiffTableModel | null;
  /** 반대편(src↔trg) 매칭 테이블 id. 클릭 시 매칭 셀도 함께 포커스한다. */
  pairTableId?: string;
  side: "src" | "trg";
}) => (
  <div className="flex-1 min-w-0 overflow-x-auto">
    {label && (
      <div className="caption-2-medium text-label-alternative mb-1 truncate">
        {label}
      </div>
    )}
    {table ? (
      <PreviewDiffTable table={table} pairTableId={pairTableId} side={side} />
    ) : (
      <div className="flex h-full min-h-[120px] items-center justify-center rounded-[6px] border border-dashed border-line-normal bg-background-neutral-alternative">
        <span className="caption-2-regular text-label-assistive">
          (테이블 없음)
        </span>
      </div>
    )}
  </div>
);

/** 카드가 뷰포트 근처에 도달했다고 볼 사전 렌더 여유(위/아래). */
const REVEAL_MARGIN = "1200px";

/**
 * 카드가 뷰포트 ±REVEAL_MARGIN 안에 들어올 때까지 무거운 테이블(셀 트리)을 마운트하지
 * 않는다. 연결/별도 전환 시 화면 밖 표 수백 개를 동시에 마운트하던 비용을 제거한다.
 *
 * - 한 번 보이면 계속 유지(`shown` 비가역) — 스크롤 도중 재마운트/위치 점프를 막는다.
 * - 미마운트 동안 min-height 로 높이를 예약해 스크롤바·레이아웃이 안정적이다(예약 높이는
 *   CARD_CLASS 의 contain-intrinsic-size 와 맞춘다).
 * - hidden(변경 사항 필터로 숨김)이면 관찰하지 않는다 — display:none 이라 마운트 불필요.
 */
const PairCard = ({
  hasChanges,
  hidden,
  children,
}: {
  hasChanges: boolean;
  hidden: boolean;
  children: React.ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown || hidden) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: `${REVEAL_MARGIN} 0px` },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shown, hidden]);

  return (
    <div
      ref={ref}
      data-dl-item
      data-dl-has-changes={hasChanges ? "true" : "false"}
      className={cn(CARD_CLASS, hidden && "hidden", !shown && "min-h-[500px]")}
    >
      {shown && <div className="flex gap-6">{children}</div>}
    </div>
  );
};

export const PreviewDiffSection = memo(function PreviewDiffSection({
  toc,
  mode,
}: {
  toc: PreviewDiffTocChunk;
  mode: PreviewDiffViewMode;
}) {
  const sectionHidden = mode === "changes" && !tocHasChanges(toc);

  return (
    <section
      data-section-id={toc.tocId}
      data-dl-section-group
      className={cn(
        "flex flex-col gap-3 scroll-mt-5",
        sectionHidden && "hidden",
      )}
    >
      <h2 className="label-1-normal-semibold text-label-strong px-1">
        {toc.displayLabel}
      </h2>

      {toc.tablePairs.map((pair, index) => {
        const changed = pairHasChanges(pair);
        return (
          <PairCard
            key={`pair-${index}`}
            hasChanges={changed}
            hidden={mode === "changes" && !changed}
          >
            <Panel
              label={pair.srcLabel}
              table={pair.alignedSrc}
              pairTableId={pair.alignedTrg?.id}
              side="src"
            />
            <Panel
              label={pair.trgLabel}
              table={pair.alignedTrg}
              pairTableId={pair.alignedSrc?.id}
              side="trg"
            />
          </PairCard>
        );
      })}

      {toc.removedTables.map((table, index) => (
        <PairCard key={`removed-${index}`} hasChanges hidden={false}>
          <Panel label="(삭제)" table={table} side="src" />
          <Panel label="" table={null} side="trg" />
        </PairCard>
      ))}

      {toc.addedTables.map((table, index) => (
        <PairCard key={`added-${index}`} hasChanges hidden={false}>
          <Panel label="" table={null} side="src" />
          <Panel label="(추가)" table={table} side="trg" />
        </PairCard>
      ))}
    </section>
  );
});
